import {
    WebzComponent,
    BindVisibleToBoolean,
    BindValue,
    Click,
} from "@boots-edu/webz";
import html from "./grid.component.html";
import css from "./grid.component.css";
import { SlotComponent } from "../slot/slot.component";
import { dealRandom, CardID, dealEasy } from "../card";

/**
 *  The game board that holds all of the cards
 *  @export
 */
export class GridComponent extends WebzComponent {
    /**
     *  Holds the slots that identify cards with images
     */
    private cards: SlotComponent[][] = [[], [], [], []];
    /**
     *  Holds the value of card selected or undefined if no card is selected
     */
    private active: SlotComponent | undefined = undefined;
    /**
     *  Default false: True when game is over and triggers game over screen to show
     */
    @BindVisibleToBoolean("game-over")
    private gameOver: boolean = false;
    /**
     *  Holds the current number of slots in the correct position
     */
    @BindValue("score", (score: number): string => `You scored: ${score}`)
    private score: number = 0;
    constructor() {
        super(html, css);
    }
    /**
     * Holds the list of moves made per game
     */
    private recent: SlotComponent[][] = [];
    /**
     * Stores easy mode value
     */
    private easyMode: boolean = false;
    /**
     * Indicates to spreadDeck not to deal new cards
     */
    private replayGame: boolean = false;

    /**
     * Puts the user in easy mode (all boards generate with a possible solution, unlimited undos)
     */
    setEasy(easy: boolean) {
        this.easyMode = easy;
        console.log("I am board, I know we are in easy mode " + this.easyMode);
        this.spreadDeck();
        this.devShow();
    }

    /**
     *  Sets the member variables of the grid to the default/empty.
     */
    resetValues(): void {
        for (let i = 0; i < 4; i++) {
            for (let j = 0; j < 13; j++) {
                if (this.cards[i][j]) {
                    this.removeComponent(this.cards[i][j]);
                }
            }
        }
        this.cards = [[], [], [], []];
        this.active = undefined;
        this.gameOver = false;
        this.score = 0;
        this.recent = [];
    }
    /**
     *  Populates this.cards with SlotComponent with card IDs.
     *  Adds clickEvent to each SlotComponent.
     *  On click assigns to active if there is not already a selected slot.
     *  Calls this.loss to check if the deal order resulted in loss.
     */
    @Click("reset")
    spreadDeck(): void {
        this.resetValues();
        let dealtDeck: CardID[];
        if (this.easyMode) dealtDeck = dealEasy(this.replayGame);
        else dealtDeck = dealRandom();

        for (let row = 0; row < 4; row++) {
            for (let column = 0; column < 13; column++) {
                const mySlot = new SlotComponent(
                    row,
                    column,
                    new CardID(
                        dealtDeck[row * 13 + column].getRank(),
                        dealtDeck[row * 13 + column].getSuit(),
                    ),
                );

                mySlot.clickEvent.subscribe((slot: SlotComponent) => {
                    if (this.active) {
                        if (this.active.getID() === slot.getID()) {
                            this.active = undefined;
                            this.findHint();
                        } else if (this.isValid(slot)) {
                            //make move
                            const temp = this.active; // funky order because of end highlight
                            this.active = undefined;
                            this.findHint();
                            this.swap(temp, slot);
                        }
                    } else {
                        this.active = slot;
                        this.findHint();
                        slot.setHighlight("gold");
                    }
                });
                this.cards[row].push(mySlot);
                this.addComponent(mySlot, "slots");
            }
        }
        this.loss();
    }

    @Click("replay")
    replay(): void {
        console.log("replay call");
        this.replayGame = true;
        this.spreadDeck();
        this.replayGame = false;
    }

    /**
     *  Checks if the active slot and the passed slot can be swapped with each other.
     *  One must be empty. The card previous to the empty slot must be the same suit and -1 rank.
     *  @param {SlotComponent} newSlot Slot clicked by user attempting to swap with active
     *  @returns {boolean} If slots can be swapped
     */
    isValid(newSlot: SlotComponent): boolean {
        let beforeEmpty: SlotComponent;
        let notEmpty: SlotComponent;
        if (!this.active!.getID()) {
            // not true => null => empty
            if (this.active!.getColumn() === 0) {
                //king case
                if (newSlot.getID()!.getRank() === 13) {
                    return true;
                } else {
                    return false;
                }
            }
            beforeEmpty =
                this.cards[this.active!.getRow()][this.active!.getColumn() - 1];
            notEmpty = newSlot;
        } else if (!newSlot.getID()) {
            // empty
            if (newSlot.getColumn() === 0) {
                //king case
                if (this.active!.getID()!.getRank() === 13) {
                    return true;
                } else {
                    return false;
                }
            }
            beforeEmpty = this.cards[newSlot.getRow()][newSlot.getColumn() - 1];
            notEmpty = this.active!;
        } else {
            return false;
        }
        if (beforeEmpty.getID()) {
            // not null
            if (
                beforeEmpty.getID()!.getSuit() === notEmpty.getID()!.getSuit()
            ) {
                if (
                    beforeEmpty.getID()!.getRank() ===
                    notEmpty.getID()!.getRank() + 1
                ) {
                    return true;
                }
            }
        }
        return false;
    }
    /**
     *  Updates this.cards places of slot1 and slot2 by swapping values. Assigns recent to the last swap
     *  @param {SlotComponent} slot1 Slot clicked by user or active slot
     *  @param {SlotComponent} slot2 Slot clicked by user or active slot (not slot1)
     */
    swap(slot1: SlotComponent, slot2: SlotComponent): void {
        this.recent.push([slot1, slot2]);
        const temp: CardID | null = slot1.getID();
        this.cards[slot1.getRow()][slot1.getColumn()].setID(slot2.getID());
        this.cards[slot2.getRow()][slot2.getColumn()].setID(temp);
        this.loss();
    }
    /**
     *  Calls the reverse of the swap that just occurred
     *  If easy mode pops the swap of the undo from the list of moves so undos infinite
     */
    undo(): void {
        if (this.recent.length > 0) {
            const moves: SlotComponent[] = this.recent.pop()!;
            this.swap(moves[0], moves[1]);
            if (this.easyMode) this.recent.pop()!;
        }
    }

    /**
     *  Iterates through this.cards counting how many slots cannot be used (behind a 2 or an empty slot).
     *  If the number of dead slots equals 4, ends the game.
     */
    loss(): void {
        let dead = 0;
        for (let row = 0; row < 4; row++) {
            for (let column = 1; column < 13; column++) {
                // can start at 1 because it will never be loss if empty is in the first column
                if (!this.cards[row][column].getID()) {
                    // empty slot after 2 case
                    if (this.cards[row][column - 1].getID()?.getRank() === 2) {
                        dead++;
                    } else if (!this.cards[row][column - 1].getID()) {
                        dead++;
                    }
                }
            }
        }
        if (dead === 4) {
            this.gameOver = true;
            this.countScore();
        }
    }
    /**
     *  Counts the number of cards in order starting from King in every row.
     *  Updates this.score with the new value.
     */
    countScore(): void {
        let total = 0;
        for (let row = 0; row < 4; row++) {
            let rowTotal = 0;
            if (this.cards[row][0].getID()?.getRank() === 13) {
                this.cards[row][0].setHighlight("magenta");
                const suit = this.cards[row][0].getID()!.getSuit();
                rowTotal = 1;
                for (let column = 1; column < 13; column++) {
                    if (
                        this.cards[row][column].getID() &&
                        this.cards[row][column].getID()!.getRank() ===
                            this.cards[row][column - 1].getID()!.getRank() -
                                1 &&
                        this.cards[row][column].getID()!.getSuit() === suit
                    ) {
                        this.cards[row][column].setHighlight("magenta");
                        rowTotal++;
                    } else if (
                        // last card is empty +1
                        !this.cards[row][column].getID() &&
                        column == 12
                    ) {
                        this.cards[row][column].setHighlight("magenta");
                        rowTotal++;
                    } else {
                        break;
                    }
                }
                total += rowTotal;
            }
        }
        this.score = total;
    }
    /**
     *  Identifies corresponding cards to highlight in respective colors
     *  Sets all highlights to transparent if called when nothing is active
     */
    findHint(): void {
        for (let row = 0; row < 4; row++) {
            for (let column = 0; column < 13; column++) {
                if (this.active === undefined) {
                    this.cards[row][column].setHighlight("transparent");
                } else if (this.active!.getID()) {
                    // Card exists => where it can go
                    const target = this.active!.getID()!;
                    const test = this.cards[row][column];
                    //king case
                    if (target.getRank() === 13) {
                        if (test.getColumn() === 0 && test.getID() === null) {
                            this.cards[row][column].setHighlight("green");
                        } else if (test.getColumn() === 0) {
                            this.cards[row][column].setHighlight("red");
                        }
                    } else if (
                        test.getID() &&
                        test.getID()!.getSuit() === target.getSuit() &&
                        test.getID()!.getRank() === target.getRank() + 1
                    ) {
                        if (test.getColumn() + 1 < 13) {
                            if (
                                this.cards[test.getRow()][
                                    test.getColumn() + 1
                                ].getID()
                            ) {
                                // Card => where it would have to go (Red)
                                this.cards[test.getRow()][
                                    test.getColumn() + 1
                                ].setHighlight("red");
                            } else {
                                // Card => where it can go (green)
                                this.cards[test.getRow()][
                                    test.getColumn() + 1
                                ].setHighlight("green");
                            }
                        }
                    }
                } else {
                    // empty on left => all kings (Green)
                    if (this.active!.getColumn() === 0) {
                        if (this.cards[row][column].getID()?.getRank() === 13) {
                            this.cards[row][column].setHighlight("green");
                        }
                    } else {
                        // empty => the card that can go there (green)
                        const target =
                            this.cards[this.active!.getRow()][
                                this.active!.getColumn() - 1
                            ].getID();
                        const test = this.cards[row][column].getID();
                        if (
                            target &&
                            test &&
                            test.getSuit() === target.getSuit() &&
                            test.getRank() === target.getRank() - 1
                        ) {
                            this.cards[row][column].setHighlight("green");
                        }
                    }
                }
            }
        }
    }
    /**
     *  Identifies dead ends for better testing of guarantee feature
     */
    devShow(): void {
        for (let row = 0; row < 4; row++) {
            for (let column = 0; column < 13; column++) {
                //recursive? idk maybe I need to think about what I actually want this to do :()
            }
        }
    }
}
